<template>
  <section class="w-100 section-gap-bottom">
    <h2
      class="section-heading f-24 sz-red--text font-weight-medium text-center pre-wrap"
      :class="{ 'font-weight-bold': $vuetify.display.xs }"
    >
      {{ title }}
    </h2>
    <div class="d-flex flex-wrap justify-space-around">
      <div v-for="(item, index) in steps" :key="index" class="step">
        <div :class="{ 'sz-primary-background': !plainTiles && !circleIcons }" class="pb-3">
          <div class="mb-0 mt-sm-0 d-flex flex-column align-start">
            <h3
              class="step__title d-flex justify-left sz-red--text font-weight-medium pt-4 px-5 mb-0"
              style="font-size: 24px"
            >
              {{ t('label.step') }} {{ index + 1 }}.
            </h3>
          </div>
          <div class="step__icon mb-0 mt-sm-0 d-flex flex-column align-center">
            <div v-if="circleIcons" class="col col-12 d-flex justify-center">
              <div class="step__circle">
                <img class="step__circle-icon" :src="`${basePath}${item.imagePath}`" alt="" />
              </div>
            </div>
            <v-img
              v-else
              class="col col-12"
              position="center"
              :src="`${basePath}${item.imagePath}`"
              contain
              height="100"
              width="113"
            />
          </div>
        </div>
        <div class="step__text mb-5 mt-sm-0 d-flex flex-column align-center">
          <div v-orphans class="f-18 sz-primary-grey--text font-weight-medium mt-5 mb-2 pre-wrap">
            <div :class="{ 'text-center': centerStepsText }" v-html="item?.text" />
          </div>
        </div>
      </div>
    </div>
    <v-row no-gutters justify="space-around" align="center" class="mt-5 ga-4">
      <BaseButton :href="LINKS.guide" variant="secondary" arrow>
        {{ guideButtonText }}
      </BaseButton>
      <BaseButton v-if="!isSwrOpen" v-orphans :href="LINKS.aboutSzp" arrow>
        {{ t('label.readMoreSzp') }}
      </BaseButton>
    </v-row>
  </section>
</template>

<script setup lang="ts">
import BaseButton from '@/components/widgets/BaseButton.vue'
import { LINKS } from '@/constants/externalLinks'
import type { Step } from '@/types'
import useEditionStageStore from '@/stores/EditionStage'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const editionStageStore = useEditionStageStore()

const { isSwrOpen } = storeToRefs(editionStageStore)

type Props = {
  title?: string
  steps?: Step[]
  guideButtonText?: string
  centerStepsText?: boolean
  // no grey tile background - for icons that already have their own red circle
  plainTiles?: boolean
  // icon (red stroke in the SVG) drawn in white on a red circle, without the grey tile background
  circleIcons?: boolean
}

withDefaults(defineProps<Props>(), {
  title: '',
  steps: () => [],
  guideButtonText: '',
  centerStepsText: false,
  plainTiles: false,
  circleIcons: false
})

const basePath = import.meta.env.VITE_PUBLIC_PATH ?? '/'
</script>

<style lang="scss">
.step {
  width: 240px;
}

// Mobile: no icons, the text takes the whole width instead of a narrow 240px column
@media (width < 600px) {
  .step {
    width: 100%;
  }

  .step__icon {
    display: none !important; // overrides `d-flex` of the wrapper
  }

  .step__text {
    align-items: stretch !important;
  }

  .step__title {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}

// Circle like in the "after choice" icons (stepsAfter): 100px, #e22226, white icon
.step__circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #e22226;
}

.step__circle-icon {
  width: 62px;
  height: 62px;
  object-fit: contain;

  // the SVG has a red stroke - on the circle it has to be white
  filter: brightness(0) invert(1);
}
</style>
